import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Role } from '@trellis/shared';
import { api, ApiError, setAccessToken, setSessionLostHandler } from './api';
import { demoSession, isDemoMode, startDemo, stopDemo } from './demo';

export interface SessionUser {
  id: string;
  role: Role;
  fullName: string | null;
  username: string;
  email?: string | null;
  tenantSlug: string | null;
  patientId: string | null;
  mustChangePassword: boolean;
}

interface LoginResponse {
  accessToken: string;
  user: SessionUser;
}

/** Un programa entre los que elegir cuando la misma cuenta sirve para varios. */
export interface ProgramChoice {
  slug: string;
  name: string;
  logoUrl: string | null;
}

/**
 * Resultado de un intento de ingreso.
 *
 * Es una unión y no un `SessionUser` opcional a propósito: cuando la misma
 * credencial vale en más de un programa la API **no abre sesión**, devuelve la
 * lista para elegir. Modelarlo así obliga a que cada pantalla de ingreso
 * contemple ese caso en vez de asumir que siempre hay usuario.
 */
export type LoginResult =
  | { kind: 'session'; user: SessionUser }
  | { kind: 'choose'; choices: ProgramChoice[] };

interface AuthContextValue {
  user: SessionUser | null;
  /** true mientras se intenta recuperar la sesión al cargar la página. */
  loading: boolean;
  login: (
    slug: string | null,
    username: string,
    password: string,
    /** Solo con DEV_AUTH_BYPASS: con qué rol entrar si no se indicó usuario. */
    devRole?: Role,
  ) => Promise<LoginResult>;
  loginWithEntra: (idToken: string) => Promise<SessionUser>;
  /** Abre la recorrida con datos de ejemplo. No sale a la red. */
  startSimulation: () => void;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Sale de la recorrida y borra lo que dejó en el camino.
   *
   * No alcanza con apagar el flag: lo que se pidió durante la simulación quedó
   * cacheado, y sin vaciarlo alguien entra con su cuenta real y sigue viendo
   * el nombre y los datos de la demostración sobre información verdadera. Es
   * peor que cualquiera de los dos mundos por separado.
   */
  const salirDeLaSimulacion = useCallback(() => {
    if (!isDemoMode()) return;
    stopDemo();
    queryClient.clear();
  }, [queryClient]);

  /**
   * Guarda la sesión y devuelve quién entró.
   *
   * Verifica que la respuesta traiga usuario y token en vez de confiar. Una
   * sesión sin usuario no es una sesión, y antes devolvía `undefined` en
   * silencio: el error aparecía tres capas más arriba, al leer `.role`, como
   * "cannot read properties of undefined" — un mensaje que no dice ni qué pidió
   * ni qué contestó el servidor. Falla acá, con el cuerpo entero en la consola.
   */
  const applySession = useCallback((data: LoginResponse) => {
    if (!data?.accessToken || !data?.user) {
      console.error('La API devolvió una sesión incompleta:', data);
      throw new ApiError(
        502,
        'La sesión volvió incompleta del servidor. Probá de nuevo; si sigue, avisá al equipo técnico',
      );
    }

    setAccessToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  /**
   * Al cargar la página se intenta renovar con la cookie httpOnly. Es lo que
   * hace que un F5 no eche al usuario, sin haber guardado nada en localStorage.
   *
   * Va por `api.refresh()` y no por un POST suelto a propósito: esa función
   * comparte la promesa en curso. En modo estricto React monta este efecto dos
   * veces, y como el refresh token rota en cada uso, dos llamadas sueltas
   * significan que la segunda encuentra el token ya revocado y cierra la sesión
   * que la primera acababa de abrir.
   */
  useEffect(() => {
    let cancelled = false;

    /**
     * En simulación la sesión se planta y no se pide.
     *
     * Va antes del refresh porque el flag sobrevive a recargar la página: quien
     * está recorriendo la demo y aprieta F5 tiene que seguir adentro, y no hay
     * cookie con la que renovar nada.
     */
    if (isDemoMode()) {
      applySession(demoSession());
      setLoading(false);
      return;
    }

    api
      .refresh()
      .then((session) => {
        if (cancelled) return;
        if (session) applySession(session as LoginResponse);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applySession]);

  // Si el refresh falla en medio de la navegación, se limpia el usuario para que
  // el router mande al login en vez de dejar pantallas a medio cargar.
  useEffect(() => {
    setSessionLostHandler(() => setUser(null));
  }, []);

  const login = useCallback(
    async (slug: string | null, username: string, password: string, devRole?: Role) => {
      // Entrar con una cuenta de verdad es salir de la recorrida.
      salirDeLaSimulacion();

      const data = await api.post<LoginResponse | { choices: ProgramChoice[] }>('/auth/login', {
        ...(slug ? { slug } : {}),
        username,
        password,
        ...(devRole ? { devRole } : {}),
      });

      // Sin accessToken no hubo sesión: la API está pidiendo que se elija programa.
      if ('choices' in data) return { kind: 'choose' as const, choices: data.choices };

      return { kind: 'session' as const, user: applySession(data) };
    },
    [applySession, salirDeLaSimulacion],
  );

  const loginWithEntra = useCallback(
    async (accessToken: string) => {
      // Igual que con usuario y clave: quien vuelve autenticado de Microsoft ya
      // no está recorriendo la demostración.
      salirDeLaSimulacion();

      const data = await api.post<LoginResponse>('/auth/entra', { accessToken });
      return applySession(data);
    },
    [applySession, salirDeLaSimulacion],
  );

  const startSimulation = useCallback(() => {
    startDemo();
    applySession(demoSession());
  }, [applySession]);

  const logout = useCallback(async () => {
    // Salir de la simulación no es cerrar sesión: no hay nada que revocar del
    // otro lado, y el pedido ni siquiera saldría a la red.
    if (isDemoMode()) {
      salirDeLaSimulacion();
      setAccessToken(null);
      setUser(null);
      return;
    }

    try {
      await api.post('/auth/logout');
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  }, [salirDeLaSimulacion]);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      const data = await api.post<LoginResponse>('/auth/change-password', {
        // Vacío en el primer ingreso: ahí la API no la pide, porque la sesión ya
        // prueba que la persona conocía la clave temporal.
        ...(currentPassword ? { currentPassword } : {}),
        newPassword,
      });
      applySession(data);
    },
    [applySession],
  );

  const value = useMemo(
    () => ({ user, loading, login, loginWithEntra, startSimulation, logout, changePassword }),
    [user, loading, login, loginWithEntra, startSimulation, logout, changePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth tiene que usarse dentro de <AuthProvider>');
  return context;
}
